import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { API_MAX_PAGE_LIMIT } from '@/common/constants';

const CONCURRENCY_LIMIT = 4;

export const fetchIncrementalBatch = async <T>(
  queryClient: QueryClient,
  itemIds: number[],
  {
    queryKeyFn,
    apiFn,
    batchSize = 50,
    staleTime = 0,
  }: {
    queryKeyFn: (itemId: number) => QueryKey;
    apiFn: (ids: number[]) => Promise<Record<number, T>>;
    batchSize?: number;
    staleTime?: number;
  },
): Promise<Record<number, T>> => {
  if (itemIds.length === 0) {
    return {};
  }

  const uniqueIds = [...new Set(itemIds)];
  const uncachedIds: number[] = [];
  const result: Record<number, T> = {};

  for (const itemId of uniqueIds) {
    const queryKey = queryKeyFn(itemId);
    const cached = queryClient.getQueryData<T | null>(queryKey);

    if (cached === undefined) {
      uncachedIds.push(itemId);
      continue;
    }

    const queryState = queryClient.getQueryState(queryKey);
    const isStale = !queryState || queryState.dataUpdatedAt <= Date.now() - staleTime;

    if (isStale) {
      uncachedIds.push(itemId);
    } else if (cached !== null) {
      result[itemId] = cached;
    }
  }

  if (uncachedIds.length > 0) {
    const chunks = uncachedIds.reduce((acc: number[][], _, index) => {
      if (index % batchSize === 0) {
        acc.push(uncachedIds.slice(index, index + batchSize));
      }
      return acc;
    }, []);

    const responses = [];

    for (let i = 0; i < chunks.length; i += CONCURRENCY_LIMIT) {
      const batch = chunks.slice(i, i + CONCURRENCY_LIMIT);
      const batchResponses = await Promise.all(batch.map((chunk) => apiFn(chunk)));
      responses.push(...batchResponses);
    }

    const fetchedIds = new Set<number>();
    for (const response of responses) {
      for (const [itemIdStr, value] of Object.entries(response)) {
        const itemId = Number(itemIdStr);
        queryClient.setQueryData(queryKeyFn(itemId), value);
        result[itemId] = value;
        fetchedIds.add(itemId);
      }
    }

    for (const itemId of uncachedIds) {
      if (!fetchedIds.has(itemId)) {
        queryClient.setQueryData(queryKeyFn(itemId), null);
      }
    }
  }

  return result;
};

export const fetchIncrementalBatchArray = async <T extends { id: number }>(
  queryClient: QueryClient,
  itemIds: number[],
  {
    queryKeyFn,
    apiFn,
    batchSize = API_MAX_PAGE_LIMIT,
    staleTime = 0,
  }: {
    queryKeyFn: (itemId: number) => QueryKey;
    apiFn: (ids: number[]) => Promise<T[]>;
    batchSize?: number;
    staleTime?: number;
  },
): Promise<T[]> => {
  if (itemIds.length === 0) {
    return [];
  }

  const uniqueIds = [...new Set(itemIds)];
  const uncachedIds: number[] = [];
  const result = new Map<number, T>();

  for (const itemId of uniqueIds) {
    const queryKey = queryKeyFn(itemId);
    const cached = queryClient.getQueryData<T | null>(queryKey);

    if (cached === undefined) {
      uncachedIds.push(itemId);
      continue;
    }

    const queryState = queryClient.getQueryState(queryKey);
    const isStale = !queryState || queryState.dataUpdatedAt <= Date.now() - staleTime;

    if (isStale) {
      uncachedIds.push(itemId);
    } else if (cached !== null) {
      result.set(itemId, cached);
    }
  }

  if (uncachedIds.length > 0) {
    const chunks = uncachedIds.reduce((acc: number[][], _, index) => {
      if (index % batchSize === 0) {
        acc.push(uncachedIds.slice(index, index + batchSize));
      }
      return acc;
    }, []);

    const responses = [];

    for (let i = 0; i < chunks.length; i += CONCURRENCY_LIMIT) {
      const batch = chunks.slice(i, i + CONCURRENCY_LIMIT);
      const batchResponses = await Promise.all(batch.map((chunk) => apiFn(chunk)));
      responses.push(...batchResponses);
    }

    const fetchedIds = new Set<number>();
    for (const response of responses) {
      for (const item of response) {
        queryClient.setQueryData(queryKeyFn(item.id), item);
        result.set(item.id, item);
        fetchedIds.add(item.id);
      }
    }

    for (const itemId of uncachedIds) {
      if (!fetchedIds.has(itemId)) {
        queryClient.setQueryData(queryKeyFn(itemId), null);
      }
    }
  }

  return Array.from(result.values());
};
